import { useState, useCallback } from 'react';
import { getApiBaseUrl, createApiHeaders } from '../../../../config/api';
import { debugWarn } from '../../../../utils/debugLog';

/**
 * Hook for managing user feedback.
 *
 * @param {string} templateId - Template ID
 * @returns {Object} Feedback data, dialog state, and feedback functions
 */
export const useUserFeedback = templateId => {
  const [userFeedbacks, setUserFeedbacks] = useState([]);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState(false);
  const [newFeedback, setNewFeedback] = useState({
    rating: 0,
    comment: '',
  });

  const loadUserFeedbacks = useCallback(async () => {
    if (!templateId) {
      setUserFeedbacks([]);
      return;
    }

    try {
      const headers = createApiHeaders();
      // Try analytics endpoint first for feedback
      const response = await fetch(
        `${getApiBaseUrl()}/api/v1/templates/analytics/templates/${templateId}`,
        { headers }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.template) {
          const analytics = data.template;
          // Extract feedback from analytics if available
          // Analytics might have feedback_analytics or feedbacks field
          const feedbacks =
            analytics.feedbacks ||
            analytics.feedback_analytics?.feedbacks ||
            analytics.recent_feedbacks ||
            [];

          // Transform feedback data to match expected format
          const transformedFeedbacks = feedbacks.map((fb, idx) => ({
            id: fb.id || fb.feedback_id || idx,
            user: fb.user || fb.user_name || fb.username || 'Anonymous',
            rating: fb.rating || fb.rating_value || 0,
            comment: fb.comment || fb.feedback_text || '',
            timestamp:
              fb.timestamp ||
              fb.created_at ||
              fb.date ||
              new Date().toISOString(),
          }));

          setUserFeedbacks(transformedFeedbacks);
          return;
        }
      }

      // Fallback: try dedicated feedback endpoint if available
      try {
        const feedbackResponse = await fetch(
          `${getApiBaseUrl()}/api/v1/templates/feedback/${templateId}`,
          { headers }
        );
        if (feedbackResponse.ok) {
          const feedbackData = await feedbackResponse.json();
          setUserFeedbacks(feedbackData.feedbacks || feedbackData || []);
          return;
        }
      } catch (err) {
        // Ignore feedback endpoint errors
      }

      // If no feedback available, set empty array
      setUserFeedbacks([]);
    } catch (error) {
      debugWarn('Failed to load feedback:', error);
      setUserFeedbacks([]);
    }
  }, [templateId]);

  const submitFeedback = useCallback(async () => {
    try {
      const feedback = {
        id: Date.now(),
        user: 'Current User',
        rating: newFeedback.rating,
        comment: newFeedback.comment,
        timestamp: new Date().toISOString(),
      };

      setUserFeedbacks(prev => [feedback, ...prev]);
      setNewFeedback({ rating: 0, comment: '' });
      setFeedbackDialogOpen(false);
      return 'Feedback submitted successfully!';
    } catch (error) {
      throw new Error('Failed to submit feedback');
    }
  }, [newFeedback]);

  return {
    userFeedbacks,
    feedbackDialogOpen,
    setFeedbackDialogOpen,
    newFeedback,
    setNewFeedback,
    loadUserFeedbacks,
    submitFeedback,
  };
};
